import { useState, useEffect } from 'react'
import Portal from '../components/Portal'
import {
    Server,
    Plus,
    Trash2,
    RefreshCw,
    Copy,
    CheckCircle,
    XCircle,
    Activity,
    Shield,
    Clock,
    Pause,
    Play,
    Search,
    Settings,
    AlertTriangle,
    Globe,
    Mail,
    Zap,
    Wifi,
    Info,
    ChevronRight,
    ServerCrash,
    FileText,
    Download
} from 'lucide-react'
import { apiService } from '../config/api'
import toast from 'react-hot-toast'
import { useAction } from '../contexts/ActionContext'

export default function Satellites() {
    const { runAction } = useAction();
    const [satellites, setSatellites] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isAdding, setIsAdding] = useState(false)
    const [newNodeName, setNewNodeName] = useState('')
    const [newNodeIp, setNewNodeIp] = useState('')
    const [newNodeHelo, setNewNodeHelo] = useState('')
    const [newNodeMailFrom, setNewNodeMailFrom] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [isRefreshingDNS, setIsRefreshingDNS] = useState({})
    const [isCheckingBlacklist, setIsCheckingBlacklist] = useState({})
    const [isResettingReliability, setIsResettingReliability] = useState({})
    const [isCheckingPort25, setIsCheckingPort25] = useState({})
    const [isTogglingPause, setIsTogglingPause] = useState({})
    const [isDeletingNode, setIsDeletingNode] = useState({})
    const [port25Results, setPort25Results] = useState({})
    const [showGuide, setShowGuide] = useState(false)
    const [createdNodeConfig, setCreatedNodeConfig] = useState(null)
    const [masterApiUrl, setMasterApiUrl] = useState('')

    const [editingNode, setEditingNode] = useState(null)
    const [configFormData, setConfigFormData] = useState({ mail_from_config: '', helo_config: '', daily_limit: '' })

    useEffect(() => {
        loadSatellites()
    }, [])

    const loadSatellites = async () => {
        try {
            setIsLoading(true)
            const response = await apiService.getSatellites()
            if (response.data.success) {
                setSatellites(response.data.data)
                if (response.data.masterApiUrl) {
                    setMasterApiUrl(response.data.masterApiUrl)
                }
            }
        } catch (error) {
            toast.error('Failed to load nodes')
        } finally {
            setIsLoading(false)
        }
    }

    const filteredSatellites = satellites.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.ip_address?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const handleTogglePause = async (id, currentPaused) => {
        if (isTogglingPause[id]) return
        setIsTogglingPause(prev => ({ ...prev, [id]: true }))
        await runAction(async () => {
            const response = await apiService.togglePauseSatellite(id, !currentPaused)
            if (response.data.success) {
                toast.success(`Node ${!currentPaused ? 'paused' : 'resumed'}`)
                loadSatellites()
            }
        }, { message: `${!currentPaused ? 'Pausing' : 'Resuming'} node...` })
        setIsTogglingPause(prev => ({ ...prev, [id]: false }))
    }

    const handleUpdateConfig = async (e) => {
        e.preventDefault()
        await runAction(async () => {
            await apiService.updateSatelliteConfig(editingNode.id, {
                mail_from_config: configFormData.mail_from_config,
                helo_config: configFormData.helo_config
            })

            const limitVal = parseInt(configFormData.daily_limit)
            if (!isNaN(limitVal)) {
                await apiService.updateSatelliteLimit(editingNode.id, limitVal)
            }

            toast.success('Configuration saved')
            setEditingNode(null)
            loadSatellites()
        }, { message: 'Saving configuration...' })
    }

    const handleRefreshDNS = async (id) => {
        if (isRefreshingDNS[id]) return
        setIsRefreshingDNS(prev => ({ ...prev, [id]: true }))
        await runAction(async () => {
            const response = await apiService.refreshSatelliteDNS(id)
            if (response.data.success) {
                toast.success('DNS checked')
                loadSatellites()
            }
        }, { message: 'Checking DNS...' })
        setIsRefreshingDNS(prev => ({ ...prev, [id]: false }))
    }

    const handleCheckBlacklist = async (id) => {
        if (isCheckingBlacklist[id]) return
        setIsCheckingBlacklist(prev => ({ ...prev, [id]: true }))
        await runAction(async () => {
            const response = await apiService.checkSatelliteBlacklist(id)
            if (response.data.success) {
                toast.success(response.data.message)
                loadSatellites()
            }
        }, { message: 'Checking blacklist...' })
        setIsCheckingBlacklist(prev => ({ ...prev, [id]: false }))
    }

    const handleResetReliability = async (id) => {
        if (isResettingReliability[id]) return
        setIsResettingReliability(prev => ({ ...prev, [id]: true }))
        await runAction(async () => {
            const response = await apiService.resetSatelliteReliability(id)
            if (response.data.success) {
                toast.success(response.data.message || 'Node fully reset')
                loadSatellites()
            }
        }, { message: 'Resetting node...' })
        setIsResettingReliability(prev => ({ ...prev, [id]: false }))
    }

    const handleCheckPort25 = async (id) => {
        if (isCheckingPort25[id]) return
        setIsCheckingPort25(prev => ({ ...prev, [id]: true }))
        setPort25Results(prev => ({ ...prev, [id]: null }))
        await runAction(async () => {
            const response = await apiService.triggerSatellitePort25(id)
            if (response.data.success) {
                // Reload satellites to get updated port25_status from DB
                await loadSatellites()
                setPort25Results(prev => ({ ...prev, [id]: response.data.data }))
            }
        }, { message: 'Testing port 25...' })
        setIsCheckingPort25(prev => ({ ...prev, [id]: false }))
    }

    const startEditing = (node) => {
        setEditingNode(node)
        setConfigFormData({
            mail_from_config: node.mail_from_config || '',
            helo_config: node.helo_config || '',
            daily_limit: node.daily_limit !== null ? node.daily_limit : 0
        })
    }

    const handleAddNode = async (e) => {
        e.preventDefault()
        if (!newNodeName.trim()) return

        await runAction(async () => {
            const response = await apiService.createSatellite({
                name: newNodeName,
                ip_address: newNodeIp,
                helo_config: newNodeHelo || '',
                mail_from_config: newNodeMailFrom || ''
            })
            if (response.data.success) {
                toast.success('Node created')
                setNewNodeName('')
                setNewNodeIp('')
                setNewNodeHelo('')
                setNewNodeMailFrom('')
                setIsAdding(false)
                setCreatedNodeConfig({
                    name: newNodeName,
                    apiKey: response.data.apiKey,
                    dotenv: response.data.dotenv
                })
                loadSatellites()
            }
        }, { message: 'Creating node...' })
    }

    const handleDeleteNode = async (id) => {
        if (!window.confirm('Delete this node?')) return
        if (isDeletingNode[id]) return
        setIsDeletingNode(prev => ({ ...prev, [id]: true }))
        await runAction(async () => {
            const response = await apiService.deleteSatellite(id)
            if (response.data.success) {
                toast.success('Node deleted')
                loadSatellites()
            }
        }, { message: 'Deleting node...' })
        setIsDeletingNode(prev => ({ ...prev, [id]: false }))
    }

    const handleResetKey = async (id) => {
        if (!window.confirm('Reset API key? This will disconnect the node.')) return
        await runAction(async () => {
            const response = await apiService.resetSatelliteKey(id)
            if (response.data.success) {
                toast.success('Key reset')
                loadSatellites()
            }
        }, { message: 'Resetting API key...' })
    }

    const copyToClipboard = (text) => {
        if (!text) return
        navigator.clipboard.writeText(text)
        toast.success('Copied')
    }

    const handleDownloadNodeEnv = (node) => {
        const protocol = window.location.protocol;
        const host = window.location.host;
        const defaultMasterApiUrl = `${protocol}//${host}/api/v1/satellite`;
        const finalMasterUrl = masterApiUrl || defaultMasterApiUrl;

        const dotenv = `NODE_NAME=${node.name}
NODE_API_KEY=${node.api_key}
MASTER_API_URL=${finalMasterUrl}`;

        const blob = new Blob([dotenv], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `.env-${node.name.toLowerCase().replace(/\s+/g, '-')}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Downloading .env for ${node.name}`);
    }

    const getDNSBadgeColor = (status) => {
        switch (status) {
            case 'ok': return 'text-emerald-500 bg-emerald-500/10'
            case 'mismatch': return 'text-amber-500 bg-amber-500/10'
            case 'missing': return 'text-rose-500 bg-rose-500/10'
            default: return 'text-slate-400 bg-slate-400/10'
        }
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
            {/* Simple Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Server className="h-6 w-6 text-brand-500" />
                        Satellite Nodes
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Manage external verification workers.</p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search..."
                            className="input pl-9 h-10 w-full sm:w-64 text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => setShowGuide(true)}
                        className="btn bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 h-10 px-4 rounded-lg text-sm gap-2"
                    >
                        <Info className="h-4 w-4" /> Usage Guide
                    </button>
                    <button
                        onClick={() => setIsAdding(!isAdding)}
                        className="btn btn-primary h-10 px-4 rounded-lg text-sm gap-2"
                    >
                        <Plus className="h-4 w-4" /> Add Node
                    </button>
                    <button
                        onClick={loadSatellites}
                        className="btn btn-secondary h-10 px-3 rounded-lg"
                    >
                        <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card p-4 flex items-center gap-4 border-l-4 border-l-brand-500">
                    <div className="h-11 w-11 rounded-2xl bg-brand-500/10 text-brand-500 flex items-center justify-center shrink-0 shadow-inner">
                        <Server className="h-5 w-5" />
                    </div>
                    <div className="overflow-hidden">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">Total Nodes</p>
                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-none mt-1">{satellites.length}</h3>
                    </div>
                </div>
                <div className="glass-card p-4 flex items-center gap-4 border-l-4 border-l-emerald-500">
                    <div className="h-11 w-11 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0 shadow-inner">
                        <Activity className="h-5 w-5" />
                    </div>
                    <div className="overflow-hidden">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">Online Now</p>
                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-none mt-1">{satellites.filter(s => s.status === 'online').length}</h3>
                    </div>
                </div>
                <div className="glass-card p-4 flex items-center gap-4 border-l-4 border-l-blue-500">
                    <div className="h-11 w-11 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0 shadow-inner">
                        <Zap className="h-5 w-5" />
                    </div>
                    <div className="overflow-hidden">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest truncate">Verified Today</p>
                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-none mt-1">{satellites.reduce((sum, s) => sum + (s.total_verified_today || 0), 0)}</h3>
                    </div>
                </div>

            </div>

            {/* Add Form */}
            {isAdding && (
                <div className="glass-card p-6 border-brand-500/20 animate-in slide-in-from-top-2 space-y-4">
                    <form onSubmit={handleAddNode} className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <input
                                type="text"
                                placeholder="Node Name *"
                                className="input flex-1 h-12"
                                value={newNodeName}
                                onChange={(e) => setNewNodeName(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="Server IP Address *"
                                className="input flex-1 h-12"
                                value={newNodeIp}
                                onChange={(e) => setNewNodeIp(e.target.value)}
                                required
                            />
                            <input
                                type="text"
                                placeholder="HELO (e.g. mta.domain.com)"
                                className="input flex-1 h-12"
                                value={newNodeHelo}
                                onChange={(e) => setNewNodeHelo(e.target.value)}
                            />
                            <input
                                type="email"
                                placeholder="FROM (e.g. verifier@domain.com)"
                                className="input flex-1 h-12"
                                value={newNodeMailFrom}
                                onChange={(e) => setNewNodeMailFrom(e.target.value)}
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-2 max-w-xl">
                                <AlertTriangle className="h-4 w-4 text-brand-500 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                                    <strong className="text-slate-700 dark:text-slate-300">Deliverability Requirement:</strong> Ensure you have correctly configured the DNS records for the HELO Domain above.
                                    A valid <strong className="text-emerald-600 dark:text-emerald-400">rDNS (PTR)</strong> record matching the HELO, and an <strong className="text-emerald-600 dark:text-emerald-400">SPF</strong> record including the node's IP are required.
                                </p>
                            </div>

                            <div className="flex gap-2 w-full sm:w-auto">
                                <button type="button" onClick={() => setIsAdding(false)} className="btn btn-secondary h-12 px-6">Cancel</button>
                                <button type="submit" className="btn btn-primary h-12 px-8">Create Node</button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {/* Nodes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSatellites.map((node) => (
                    <div key={node.id} className={`glass-card overflow-hidden border-t-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${node.is_paused
                        ? 'border-amber-500 shadow-amber-500/5 ring-1 ring-amber-500/20 opacity-60 grayscale-[0.8]'
                        : (node.status === 'online' || node.status === 'rate_limited')
                            ? 'border-emerald-500 shadow-emerald-500/10 ring-1 ring-emerald-500/20'
                            : 'border-slate-300 shadow-sm'
                        }`}>
                        {/* Card Header */}
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/30 dark:bg-slate-800/10">
                            <div className="flex flex-col gap-1 overflow-hidden">
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const isOnline = !node.is_paused && (node.status === 'online' || node.status === 'rate_limited' || node.status === 'maintenance');
                                        const statusText = node.is_paused ? 'Paused' : isOnline ? 'Online' : 'Offline';
                                        return (
                                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${isOnline
                                                ? 'bg-emerald-500/10 border border-emerald-500/20'
                                                : node.is_paused ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-slate-100/50 border border-slate-200/60'
                                                } transition-all shrink-0`}>
                                                <div className={`h-2 w-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : node.is_paused ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                                <span className={`text-[9px] font-black uppercase tracking-widest ${isOnline ? 'text-emerald-600 dark:text-emerald-400' : node.is_paused ? 'text-amber-600' : 'text-slate-500'
                                                    }`}>
                                                    {statusText}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <h3 className="font-bold text-slate-900 dark:text-white truncate tracking-tight text-sm" title={node.name}>{node.name}</h3>
                                </div>

                                {/* Rate-limited provider info — shown BELOW the status/name row */}
                                {node.last_rate_limit_info && (() => {
                                    const info = typeof node.last_rate_limit_info === 'string'
                                        ? JSON.parse(node.last_rate_limit_info)
                                        : node.last_rate_limit_info;
                                    const providers = Object.keys(info || {});
                                    // Only show if rate-limit was recent (within last 24 hours)
                                    const recentProviders = providers.filter(p => {
                                        const t = new Date(info[p]).getTime();
                                        return !isNaN(t) && (Date.now() - t) < 24 * 60 * 60 * 1000;
                                    });
                                    if (recentProviders.length === 0) return null;
                                    return (
                                        <div className="flex items-center gap-2 flex-wrap mt-1" title="Providers that recently rate-limited this node's IP"  >
                                            <div className="flex items-center gap-1">
                                                <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                <span className="text-[10px] text-amber-600 font-black uppercase tracking-tight">Active Rate Limited:</span>
                                            </div>
                                            {recentProviders.map(p => (
                                                <span key={p} className="text-[10px] font-black px-2 py-0.5 rounded-md bg-amber-500 text-white shadow-sm shadow-amber-500/20">
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex gap-1 shrink-0 ml-2">
                                <button onClick={() => startEditing(node)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-brand-500 transition-colors" title="Settings">
                                    <Settings className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleTogglePause(node.id, node.is_paused)}
                                    disabled={isTogglingPause[node.id]}
                                    className={`p-1.5 rounded-lg transition-colors ${
                                        isTogglingPause[node.id]
                                            ? 'bg-slate-200 dark:bg-slate-700 text-slate-300 cursor-not-allowed'
                                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-amber-500'
                                    }`}
                                    title={node.is_paused ? "Resume Node" : "Pause Node"}
                                >
                                    {isTogglingPause[node.id] ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-amber-500" />
                                    ) : node.is_paused ? (
                                        <Play className="h-4 w-4" />
                                    ) : (
                                        <Pause className="h-4 w-4" />
                                    )}
                                </button>
                                {node.name !== 'Master Server' && (
                                    <button
                                        onClick={() => handleDeleteNode(node.id)}
                                        disabled={isDeletingNode[node.id]}
                                        className={`p-1.5 rounded-lg transition-colors ${
                                            isDeletingNode[node.id]
                                                ? 'bg-slate-200 dark:bg-slate-700 text-slate-300 cursor-not-allowed'
                                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-rose-500'
                                        }`}
                                        title="Delete Node"
                                    >
                                        {isDeletingNode[node.id] ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-rose-500" />
                                        ) : (
                                            <Trash2 className="h-4 w-4" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-5 space-y-4">
                            {/* Blacklist Status — full width */}
                            <div className={`p-3 rounded-2xl border transition-all duration-300 flex flex-col justify-center gap-1 ${node.blacklist_status === 'blacklisted' ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-100/50 dark:border-rose-500/20' : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100/50 dark:border-emerald-500/20'}`}>
                                <span className="text-[10px] font-bold text-slate-400 uppercase leading-none tracking-tight text-left">IP Status</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    {node.blacklist_status === 'blacklisted' ? (
                                        <>
                                            <AlertTriangle className="h-3 w-3 text-rose-500" />
                                            <span className="text-[11px] font-black text-rose-600 dark:text-rose-400">Blacklisted</span>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="h-3 w-3 text-emerald-500" />
                                            <span className="text-[11px] font-black text-emerald-600 dark:text-emerald-400">Clean Status</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Warning */}
                            {
                                ((node.rdns_status !== 'ok' && node.rdns_status !== 'not_applicable') ||
                                    (node.spf_status !== 'ok' && node.spf_status !== 'not_applicable')) && (
                                    <div className="p-3 bg-rose-50 dark:bg-rose-500/10 rounded-xl flex items-start gap-3 border border-rose-100 dark:border-rose-500/20">
                                        <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                                        <div className="text-[11px] text-rose-700 dark:text-rose-400 font-medium leading-relaxed">
                                            DNS issue detected. Click "Refresh" to verify.
                                        </div>
                                    </div>
                                )
                            }

                            {/* Usage */}
                            <div className="space-y-2">
                                <div className="flex justify-between text-[11px] font-bold">
                                    <span className="text-slate-500 uppercase">Usage Today</span>
                                    <span className="text-slate-900 dark:text-white">{node.total_verified_today} / {node.daily_limit || '∞'}</span>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-800/50 rounded-full overflow-hidden ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/30">
                                    <div
                                        className="h-full bg-gradient-to-r from-brand-600 to-primary-500 transition-all duration-1000 ease-out relative"
                                        style={{ width: `${Math.min(100, (node.total_verified_today / (node.daily_limit || 10000)) * 100)}%` }}
                                    >
                                        <div className="absolute inset-0 bg-white/10 animate-pulse-slow"></div>
                                    </div>
                                </div>
                            </div>

                            {/* Provider Analytics */}
                            {
                                node.provider_stats && (
                                    <div className="p-3 bg-slate-50/50 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Provider Health</span>
                                            <span className="text-[9px] text-slate-400">(Success Rate)</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                            {Object.entries(typeof node.provider_stats === 'string' ? JSON.parse(node.provider_stats) : node.provider_stats)
                                                .filter(([p, stat]) => {
                                                    // Only show providers with actual data; hide 'custom' unless it's the only one
                                                    const parsed = typeof node.provider_stats === 'string' ? JSON.parse(node.provider_stats) : node.provider_stats;
                                                    return (p !== 'custom' || Object.keys(parsed).length < 2) && (stat.total || 0) > 0;
                                                })
                                                .slice(0, 4)
                                                .map(([provider, stat]) => {
                                                    const successRate = stat.total > 0 ? Math.round((stat.ok / stat.total) * 100) : 0;
                                                    const isLow = successRate < 60;
                                                    return (
                                                        <div key={provider} className="space-y-1">
                                                            <div className="flex justify-between text-[10px] font-medium">
                                                                <span className="capitalize text-slate-500 dark:text-slate-400 truncate pr-1">{provider}</span>
                                                                <span className={isLow ? 'text-rose-500' : 'text-emerald-500'}>{successRate}%</span>
                                                            </div>
                                                            <div className="h-1 bg-slate-200 dark:bg-slate-700/50 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-1000 ${isLow ? 'bg-rose-500' : 'bg-emerald-500'}`}
                                                                    style={{ width: `${successRate}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )
                            }

                            {/* Details List */}
                            <div className="space-y-2 text-[12px]">
                                <div className="flex justify-between py-1 border-b border-slate-50 dark:border-slate-800">
                                    <span className="text-slate-400">IP Address</span>
                                    <span className="font-bold flex items-center gap-1">
                                        <Globe className="h-3 w-3 text-brand-500" />
                                        {node.ip_address || '---'}
                                    </span>
                                </div>
                                <div className="flex justify-between py-1 border-b border-slate-50 dark:border-slate-800">
                                    <span className="text-slate-400">Last Seen</span>
                                    <span className="font-bold flex items-center gap-1">
                                        <Clock className="h-3 w-3 text-slate-400" />
                                        {node.last_ping ? (() => {
                                            const diff = Date.now() - new Date(node.last_ping).getTime();
                                            if (diff < 60000) return 'Just now';
                                            const mins = Math.floor(diff / 60000);
                                            if (mins < 60) return `${mins}m ago`;
                                            const hours = Math.floor(mins / 60);
                                            if (hours < 24) return `${hours}h ago`;
                                            return new Date(node.last_ping).toLocaleDateString();
                                        })() : 'Never'}
                                    </span>
                                </div>
                                <div className="space-y-1.5 pt-1">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase">Node Identity</p>
                                    <div className="grid grid-cols-2 gap-2 text-[10px] font-bold">
                                        <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-100 dark:border-slate-800 truncate">
                                            <span className="text-slate-400 block mb-0.5">HELO</span>
                                            <span className="truncate block" title={node.helo_config}>{node.helo_config || 'global'}</span>
                                        </div>
                                        <div className="p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-100 dark:border-slate-800 truncate">
                                            <span className="text-slate-400 block mb-0.5">FROM</span>
                                            <span className="truncate block" title={node.mail_from_config}>{node.mail_from_config || 'global'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* API Key */}
                            <div className="pt-2">
                                <label className="text-[10px] font-bold text-slate-400 uppercase mb-1 block">API Key</label>
                                <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg group">
                                    <code className="text-[10px] font-mono flex-1 truncate text-slate-600 dark:text-slate-400">{node.api_key}</code>
                                    <button onClick={() => copyToClipboard(node.api_key)} title="Copy API Key" className="p-1 hover:text-brand-500 transition-colors">
                                        <Copy className="h-3 w-3" />
                                    </button>
                                    <button onClick={() => handleDownloadNodeEnv(node)} title="Download .env for this node" className="p-1 hover:text-emerald-500 transition-colors">
                                        <Download className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        </div >

                        {/* Card Footer */}
                        < div className="px-5 py-3 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center" >
                            <div className="flex gap-2">
                                <div className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${getDNSBadgeColor(node.rdns_status)}`}>
                                    PTR: {node.rdns_status || '...'}
                                </div>
                                <div className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${getDNSBadgeColor(node.spf_status)}`}>
                                    SPF: {node.spf_status || '...'}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleRefreshDNS(node.id)}
                                    disabled={isRefreshingDNS[node.id]}
                                    title="Refresh DNS Status"
                                    className="p-1.5 text-slate-400 hover:text-brand-500 disabled:opacity-50"
                                >
                                    <RefreshCw className={`h-3.5 w-3.5 ${isRefreshingDNS[node.id] ? 'animate-spin' : ''}`} />
                                </button>
                                <button
                                    onClick={() => handleResetReliability(node.id)}
                                    disabled={isResettingReliability[node.id]}
                                    title="Reset Node Stats (reliability, health, usage, blocks)"
                                    className="p-1.5 text-slate-400 hover:text-brand-500 disabled:opacity-50"
                                >
                                    <Wifi className={`h-3.5 w-3.5 ${isResettingReliability[node.id] ? 'animate-pulse' : ''}`} />
                                </button>
                                <button
                                    onClick={() => handleCheckBlacklist(node.id)}
                                    disabled={isCheckingBlacklist[node.id]}
                                    title="Check IP Blacklist"
                                    className="p-1.5 text-slate-400 hover:text-brand-500 disabled:opacity-50"
                                >
                                    <Search className={`h-3.5 w-3.5 ${isCheckingBlacklist[node.id] ? 'animate-pulse' : ''}`} />
                                </button>
                                <button
                                    onClick={() => handleCheckPort25(node.id)}
                                    disabled={isCheckingPort25[node.id]}
                                    title="Test outbound port 25 SMTP (runs from this server's own IP)"
                                    className="p-1.5 text-slate-400 hover:text-brand-500 disabled:opacity-50"
                                >
                                    <Zap className={`h-3.5 w-3.5 ${isCheckingPort25[node.id] ? 'animate-pulse text-brand-500' : ''}`} />
                                </button>
                                {/* Show live result from this session OR stored status from DB */}
                                {(port25Results[node.id] || node.port25_status) && (() => {
                                    const live = port25Results[node.id];
                                    const isOpen = live ? live.open : node.port25_status === 'open';
                                    const msg = live ? live.message : `Port 25: ${node.port25_status?.toUpperCase()} (last checked)`;
                                    return (
                                        <div
                                            title={msg}
                                            className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full cursor-default ${isOpen
                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                }`}>
                                            {isOpen
                                                ? <><CheckCircle className="h-3 w-3" /> P25: OPEN</>
                                                : <><XCircle className="h-3 w-3" /> P25: BLOCKED</>}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div >
                    </div >
                ))
                }
            </div >

            {/* Simple Config Modal */}
            {
                editingNode && <Portal>
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                                <h3 className="text-lg font-bold">Node Identity</h3>
                                <p className="text-xs text-slate-500 mt-1">Settings for node: {editingNode.name}</p>
                            </div>
                            <form onSubmit={handleUpdateConfig} className="p-6 space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase">Mail From Address</label>
                                    <input
                                        type="email"
                                        className="input h-11 text-sm bg-slate-50 dark:bg-slate-800/50"
                                        placeholder="verifier@domain.com"
                                        value={configFormData.mail_from_config}
                                        onChange={(e) => setConfigFormData({ ...configFormData, mail_from_config: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase">HELO Domain</label>
                                    <input
                                        type="text"
                                        className="input h-11 text-sm bg-slate-50 dark:bg-slate-800/50"
                                        placeholder="mta.domain.com"
                                        value={configFormData.helo_config}
                                        onChange={(e) => setConfigFormData({ ...configFormData, helo_config: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase flex items-center justify-between">
                                        <span>Daily Verification Limit</span>
                                        <span className="text-[10px] text-slate-400 font-normal">0 = Unlimited</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        className="input h-11 text-sm bg-slate-50 dark:bg-slate-800/50 font-mono"
                                        placeholder="0 (Unlimited) or e.g. 5000"
                                        value={configFormData.daily_limit}
                                        onChange={(e) => setConfigFormData({ ...configFormData, daily_limit: e.target.value })}
                                    />
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button type="submit" className="btn btn-primary h-11 flex-1 font-bold">Save Changes</button>
                                    <button type="button" onClick={() => setEditingNode(null)} className="btn btn-secondary h-11 px-4">Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Portal>
            }

            {/* Comprehensive Guide Modal */}
            {
                showGuide && <Portal>
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col">
                            <div className="p-6 sm:px-8 sm:py-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                                        <Globe className="h-7 w-7 text-brand-500" />
                                        Satellite Network Guide
                                    </h2>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">Everything you need to know about scaling verification with Nodes.</p>
                                </div>
                                <button onClick={() => setShowGuide(false)} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                                    <XCircle className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="p-6 sm:p-8 overflow-y-auto space-y-8 flex-1 custom-scrollbar">

                                {/* Why Use Satellites */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                        <AlertTriangle className="h-5 w-5 text-emerald-500" />
                                        Why use Satellite Nodes?
                                    </h3>
                                    <div className="grid sm:grid-cols-3 gap-4">
                                        <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-2xl border border-emerald-100 dark:border-emerald-500/20">
                                            <ServerCrash className="h-6 w-6 text-emerald-600 mb-2" />
                                            <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Protect IP Reputation</h4>
                                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">Offload SMTP checks to external servers. If a node gets blocked, your Master Node remains perfectly safe.</p>
                                        </div>
                                        <div className="p-4 bg-blue-50 dark:bg-blue-500/10 rounded-2xl border border-blue-100 dark:border-blue-500/20">
                                            <Zap className="h-6 w-6 text-blue-600 mb-2" />
                                            <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Scale Volume</h4>
                                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">Bypass ESP rate limits (Gmail, Yahoo) by distributing verification workloads across multiple unique IP addresses.</p>
                                        </div>
                                        <div className="p-4 bg-purple-50 dark:bg-purple-500/10 rounded-2xl border border-purple-100 dark:border-purple-500/20">
                                            <Activity className="h-6 w-6 text-purple-600 mb-2" />
                                            <h4 className="font-bold text-slate-900 dark:text-white text-sm mb-1">Load Balancing</h4>
                                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">Turn on "Node Rotation" in settings to legally divide traffic into chunks, cycling IPs to prevent spam detections.</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Deployment Guide */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                            <Server className="h-5 w-5 text-brand-500" />
                                            Deploying a Satellite Server
                                        </h3>
                                    </div>
                                    <div className="bg-slate-900 rounded-2xl p-6 shadow-inner text-slate-300">
                                        <ol className="list-decimal list-outside ml-4 space-y-4 text-sm font-medium">
                                            <li>
                                                <strong className="text-white block mb-1">Step 1: Get a cheap VPS</strong>
                                                Purchase a fresh Ubuntu 20.04/22.04 VPS. Ensure port 25 is OPEN for outbound traffic.
                                            </li>
                                            <li>
                                                <strong className="text-white block mb-1">Step 2: Add Node in Dashboard</strong>
                                                Click <span className="text-brand-400">Add Node</span> on this page. Input your desired Node Name, HELO domain, and FROM Email. Note down the generated API Key.
                                            </li>
                                            <li>
                                                <strong className="text-white block mb-1">Step 3: Setup DNS (Crucial)</strong>
                                                Point the A record of your HELO domain to the VPS IP. Then set up the <span className="text-emerald-400">rDNS (PTR)</span> to match the HELO domain inside your VPS hosting panel.
                                            </li>
                                            <li>
                                                <strong className="text-white block mb-1">Step 4: Upload & Configure Node</strong>
                                                <p className="text-xs mt-2 mb-2 text-slate-400">Upload the <code className="text-brand-400 bg-slate-800 px-1 py-0.5 rounded">satellite-node</code> folder to your VPS. Rename <code className="text-brand-400 bg-slate-800 px-1 py-0.5 rounded">.env.example</code> to <code className="text-brand-400 bg-slate-800 px-1 py-0.5 rounded">.env</code> and fill in your Node API Key (from Step 2) and your Master Server's API URL.</p>
                                            </li>
                                            <li>
                                                <strong className="text-white block mb-1">Step 5: Install & Run</strong>
                                                <p className="text-xs mt-2 mb-2 text-slate-400">SSH into your VPS, navigate to the folder, and run the following commands to start the node:</p>
                                                <div className="bg-black/50 p-3 rounded-lg border border-slate-700 font-mono text-xs space-y-2">
                                                    <div className="text-green-400">npm install</div>
                                                    <div className="text-green-400">pm2 start worker.js --name "satellite-node"</div>
                                                </div>
                                            </li>
                                        </ol>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </Portal>
            }

            {/* Node Created Success Modal */}
            {
                createdNodeConfig && <Portal>
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                        <div className="bg-white dark:bg-slate-900 w-full max-w-xl rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
                            <div className="p-6 sm:p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-emerald-50/50 dark:bg-emerald-950/20">
                                <div>
                                    <h2 className="text-xl font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                                        <CheckCircle className="h-6 w-6" />
                                        Node Created Successfully!
                                    </h2>
                                    <p className="text-sm text-slate-500 mt-1 uppercase tracking-wider font-bold text-[10px]">
                                        Identity: {createdNodeConfig.name}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setCreatedNodeConfig(null)}
                                    className="p-2 hover:bg-white/50 dark:hover:bg-slate-800 rounded-full text-slate-400 transition-colors"
                                >
                                    <XCircle className="h-6 w-6" />
                                </button>
                            </div>

                            <div className="p-6 sm:p-8 space-y-6">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <FileText className="h-3.5 w-3.5" />
                                            Satellite .env Configuration
                                        </h4>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    copyToClipboard(createdNodeConfig.dotenv);
                                                    toast.success('Configuration copied!');
                                                }}
                                                className="text-[10px] font-bold text-brand-500 hover:text-brand-600 flex items-center gap-1 bg-brand-500/5 px-2 py-1 rounded-md"
                                            >
                                                <Copy className="h-3 w-3" /> Copy
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const blob = new Blob([createdNodeConfig.dotenv], { type: 'text/plain' });
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = '.env';
                                                    a.click();
                                                    URL.revokeObjectURL(url);
                                                    toast.success('Downloading .env file...');
                                                }}
                                                className="text-[10px] font-bold text-emerald-500 hover:text-emerald-600 flex items-center gap-1 bg-emerald-500/5 px-2 py-1 rounded-md"
                                            >
                                                <Download className="h-3 w-3" /> Download
                                            </button>
                                        </div>
                                    </div>
                                    <div className="bg-slate-950 rounded-xl p-4 font-mono text-xs text-emerald-400 border border-slate-800 shadow-inner group relative">
                                        <pre className="whitespace-pre-wrap break-all leading-relaxed">
                                            {createdNodeConfig.dotenv}
                                        </pre>
                                    </div>
                                </div>

                                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-4 flex gap-4">
                                    <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-amber-800 dark:text-amber-400">Important Next Step</p>
                                        <p className="text-[11px] text-amber-700 dark:text-amber-500/80 leading-relaxed font-medium">
                                            Place this <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">.env</code> file into your satellite node's root folder on the VPS. This file contains the private API key needed to connect back to this Master Server.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setCreatedNodeConfig(null)}
                                    className="btn btn-primary w-full h-12 rounded-xl font-bold shadow-lg shadow-brand-500/20"
                                >
                                    Got it, Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </Portal>
            }
        </div >
    )
}
